'use client';

import React, { useState } from 'react';
import { fetchQuery } from '@/utils/fetchQuery';
import { AnalysePostNode } from '@/types/analyses/page';
import { RelatedPostsCard } from '@/components/(blog)/post/RelatedPostsCard';
import { LoadingSpinner } from '@/components/Icons';

interface PageInfo {
    endCursor: string | null;
    total: number;
}

interface Posts {
    edges: { node: AnalysePostNode }[];
    pageInfo: PageInfo;
}

interface PostCardProps {
    initialPosts?: Posts;
    query: string;
    variables: { slug: string | string[] };
    initialPageInfo?: PageInfo;
    postType?: 'posts' | 'allNews';
    total: number;
    title?: string;
    /** Inside a page column (platform pages): no container padding, 2 / 3 / 4 columns */
    embedded?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({
    initialPosts,
    query,
    variables,
    initialPageInfo,
    postType = 'posts',
    total,
    title,
    embedded = false,
}) => {
    const [posts, setPosts] = useState(initialPosts?.edges || []);
    const [endCursor, setEndCursor] = useState(initialPageInfo?.endCursor || null);
    const [isLoading, setIsLoading] = useState(false);

    const loadMorePosts = async () => {
        if (!isLoading && endCursor) {
            setIsLoading(true);
            try {
                const variablesWithCursor = {
                    ...variables,
                    first: 10,
                    ...(endCursor ? { after: endCursor } : {}),
                };

                const newData = await fetchQuery<{ [key: string]: Posts }>(
                    query,
                    variablesWithCursor,
                );

                const newPostsData = newData[postType];

                if (newPostsData) {
                    const newEdges = newPostsData.edges || [];
                    setPosts((prevPosts) => [...prevPosts, ...newEdges]);
                    setEndCursor(newPostsData.pageInfo.endCursor);
                }
            } catch (error) {
                console.error('Error loading more posts:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <section className={embedded ? '' : 'container mx-auto max-lg:px-0'}>
            {/* Semantic Desktop Header */}
            <div
                className={`${embedded ? 'mb-5' : 'customContainer !px-0 mb-10'} flex justify-between border-b border-black/10 dark:border-white/10`}
            >
                <h2 className='text-md lg:text-2xl font-bold border-b-2 border-progress pb-3 text-gray-900 dark:text-white'>
                    لیست {title}
                </h2>
                <div className='flex items-center text-gray-500 dark:text-gray-400 text-xs'>
                    نمایش {posts.length} از {total} مورد
                </div>
            </div>

            {/* Responsive Grid */}
            <div
                className={
                    embedded
                        ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4'
                        : 'customContainer max-lg:px-0 grid gap-3 md:gap-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
                }
            >
                {posts.map(({ node }, index) => (
                    <article
                        key={`${node.databaseId}-${index}`}
                        className='group flex flex-col border dark:border-white/10 rounded-xl lg:rounded-2xl overflow-hidden hover:ring hover:ring-progress/30 transition-all duration-300'
                    >
                        <RelatedPostsCard
                            slug={node.uri}
                            title={node.title}
                            databaseId={node.databaseId}
                            featuredImage={node.featuredImage}
                            index={index}
                            // 2 / 3 / 4 / 5 columns
                            sizes={
                                embedded
                                    ? '(min-width: 1280px) 220px, (min-width: 1024px) 260px, (min-width: 640px) 33vw, 50vw'
                                    : '(min-width: 1280px) 240px, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw'
                            }
                        />
                    </article>
                ))}
            </div>

            {/* Smart Pagination Button */}
            {posts.length < (total || 0) && (
                <div className='mt-12 flex flex-col items-center gap-4'>
                    <button
                        onClick={loadMorePosts}
                        disabled={isLoading}
                        className='w-full md:w-auto min-w-[240px] px-8 py-4 bg-primary-hover dark:bg-progress text-white rounded-2xl font-bold shadow-lg shadow-progress/20 hover:brightness-125 active:scale-95 transition-all flex items-center justify-center gap-3'
                    >
                        {isLoading ? (
                            <LoadingSpinner className='w-5 h-5 animate-spin' />
                        ) : (
                            <>
                                <span>بارگذاری مطالب بیشتر</span>
                                <span className='text-xs bg-white/20 px-2 pt-1 rounded-md'>
                                    {total - posts.length} مانده
                                </span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </section>
    );
};

export default PostCard;
